import { useEffect, useRef, useState } from "react";
import { ArrowDown, Download, Music2, Pause, PenLine, Play } from "lucide-react";
import { toPng } from "html-to-image";
import type { DayContent } from "../types";
import { useTypewriter } from "../hooks/useTypewriter";
import { DayNavigation } from "./DayNavigation";
import { ScratchReveal } from "./ScratchReveal";

interface Props {
  day: DayContent;
  days: DayContent[];
  musicReady: boolean;
  onSelectDay: (day: DayContent) => void;
  onOpened: (dayId: string) => void;
}

export function LetterExperience({ day, days, musicReady, onSelectDay, onOpened }: Props) {
  const [opened, setOpened] = useState(false);
  const [musicPlaying, setMusicPlaying] = useState(musicReady);
  const [downloading, setDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<string | null>(null);
  const letterRef = useRef<HTMLDivElement>(null);
  const written = useTypewriter(day.letter, opened);

  useEffect(() => {
    setOpened(false);
    setMusicPlaying(musicReady);
    setDownloadStatus(null);
  }, [day.id, musicReady]);

  function openLetter() {
    playPaperRustle();
    navigator.vibrate?.([24, 35, 18]);
    setOpened(true);
    onOpened(day.id);
  }

  function continueToLetter() {
    document.getElementById("daily-letter-envelope")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function downloadLetter() {
    if (!letterRef.current) return;
    setDownloading(true);
    setDownloadStatus(null);
    try {
      const dataUrl = await toPng(letterRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#f4dfbd",
      });
      const link = document.createElement("a");
      link.download = `המכתב-של-אבי-לרבקה-${day.date.replaceAll(".", "-")}.png`;
      link.href = dataUrl;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setDownloadStatus("המכתב מוכן ונשמר כתמונה במכשיר שלך.");
    } catch {
      setDownloadStatus("לא הצלחנו לשמור הפעם. נסי שוב בעוד רגע.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <section id="daily-letter" className="letter-experience" aria-labelledby="letter-heading">
      <img className="letter-background" src={day.image} alt="" aria-hidden="true" />
      <div className="letter-backdrop" aria-hidden="true" />
      <div className="letter-shell">
        <div className="daily-memory-intro">
          <figure className="daily-memory">
            <div><img src={day.memoryImage} alt="זיכרון משותף של אבי ורבקה" /></div>
            <figcaption>
              <span>הזיכרון של היום</span>
              <p>{day.memoryCaption}</p>
            </figcaption>
          </figure>
          <button className="memory-to-letter-button" onClick={continueToLetter}>
            <span>המשיכי למכתב</span>
            <ArrowDown aria-hidden="true" />
          </button>
        </div>

        <header id="daily-letter-envelope" className="letter-heading-block">
          <p>{day.hebrewDate}</p>
          <h2 id="letter-heading">{day.title}</h2>
          <span>{day.dateLabel}</span>
        </header>

        {!opened ? (
          <button className="envelope" onClick={openLetter} aria-label={`פתחי את המכתב של יום ${day.number}`}>
            <span className="envelope-back" />
            <span className="envelope-paper">לרבקה שלי</span>
            <span className="envelope-front" />
            <span className="wax-seal">א׳ + ר׳</span>
            <strong>לחצי לפתיחת המכתב</strong>
          </button>
        ) : (
          <div className="opened-letter">
            <div ref={letterRef} className="parchment">
              <div className="parchment-ornament" aria-hidden="true">❧</div>
              <p className="letter-date">{day.hebrewDate} · {day.dateLabel}</p>
              <h3>{day.title}</h3>
              <p className="letter-copy">{written}<span className="writing-caret" aria-hidden="true" /></p>
              <ScratchReveal giftNumber={day.giftNumber} />
              {day.signatureImage ? (
                <img className="signature-image" src={day.signatureImage} alt="שלך תמיד, אבי" />
              ) : (
                <p className="signature">שלך תמיד, אבי</p>
              )}
              <PenLine className="pen-cue" aria-hidden="true" />
            </div>
            <button className="download-button" onClick={downloadLetter} disabled={downloading}>
              <Download aria-hidden="true" />
              {downloading ? "מכין את התמונה…" : "שמרי את המכתב"}
            </button>
            {downloadStatus && <p className="download-status" role="status">{downloadStatus}</p>}
          </div>
        )}

        <div className="music-panel" aria-label="נגן המוזיקה היומי">
          <button onClick={() => setMusicPlaying((value) => !value)} aria-label={musicPlaying ? "השהיית מוזיקה" : "הפעלת מוזיקה"}>
            {musicPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
          </button>
          <Music2 aria-hidden="true" />
          <div>
            <strong>השיר של היום</strong>
            <span>מתחיל ב־{formatSongStart(day.youtubeStartSeconds)}</span>
          </div>
        </div>

        {musicReady && musicPlaying && (
          <iframe
            className="youtube-player"
            src={`https://www.youtube-nocookie.com/embed/${day.youtubeId}?autoplay=1&playsinline=1&rel=0&start=${day.youtubeStartSeconds}`}
            title={`השיר של יום ${day.number}`}
            allow="autoplay; encrypted-media; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        )}

        <DayNavigation days={days} selectedId={day.id} onSelect={onSelectDay} />
      </div>
    </section>
  );
}

function formatSongStart(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainder = String(seconds % 60).padStart(2, "0");
  return `${minutes}:${remainder}`;
}

function playPaperRustle() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const AudioContextClass = window.AudioContext;
  if (!AudioContextClass) return;
  const context = new AudioContextClass();
  const duration = .34;
  const buffer = context.createBuffer(1, Math.floor(context.sampleRate * duration), context.sampleRate);
  const data = buffer.getChannelData(0);
  for (let index = 0; index < data.length; index += 1) {
    const fade = 1 - index / data.length;
    data[index] = (Math.random() * 2 - 1) * fade * .22;
  }
  const source = context.createBufferSource();
  const filter = context.createBiquadFilter();
  const gain = context.createGain();
  filter.type = "bandpass";
  filter.frequency.value = 1250;
  filter.Q.value = .7;
  gain.gain.setValueAtTime(.0001, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(.12, context.currentTime + .025);
  gain.gain.exponentialRampToValueAtTime(.0001, context.currentTime + duration);
  source.buffer = buffer;
  source.connect(filter).connect(gain).connect(context.destination);
  source.start();
  source.addEventListener("ended", () => void context.close(), { once: true });
}
